import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  UpdateProductPageComponetsMutationRequest,
  UpdateProductPageComponetsMutationResponse,
  UpdateProductPageComponetsPathParams,
} from "../types/UpdateProductPageComponets"

type UpdateProductPageComponetsClient = typeof client<
  UpdateProductPageComponetsMutationResponse,
  Error,
  UpdateProductPageComponetsMutationRequest
>
type UpdateProductPageComponets = {
  data: UpdateProductPageComponetsMutationResponse
  error: Error
  request: UpdateProductPageComponetsMutationRequest
  pathParams: UpdateProductPageComponetsPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<UpdateProductPageComponetsClient>>
  client: {
    parameters: Partial<Parameters<UpdateProductPageComponetsClient>[0]>
    return: Awaited<ReturnType<UpdateProductPageComponetsClient>>
  }
}
/**
 * @summary Update product page component content
 * @link /v1/product-page-components/:productPageComponentId
 */
export function useUpdateProductPageComponets(
  productPageComponentId: UpdateProductPageComponetsPathParams["productPageComponentId"],
  options: {
    mutation?: UseMutationOptions<
      UpdateProductPageComponets["response"],
      UpdateProductPageComponets["error"],
      UpdateProductPageComponets["request"]
    >
    client?: UpdateProductPageComponets["client"]["parameters"]
  } = {}
): UseMutationResult<
  UpdateProductPageComponets["response"],
  UpdateProductPageComponets["error"],
  UpdateProductPageComponets["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    UpdateProductPageComponets["response"],
    UpdateProductPageComponets["error"],
    UpdateProductPageComponets["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        UpdateProductPageComponets["data"],
        UpdateProductPageComponets["error"],
        UpdateProductPageComponets["request"]
      >({
        method: "post",
        url: `/v1/product-page-components/${productPageComponentId}`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
